import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { usersAPI, setToken } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, CheckCircle } from 'lucide-react';

function AcceptInvitePage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { setSession } = useAuth();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const token = searchParams.get('token');

    useEffect(() => {
        if (!token) {
            toast.error('Lien invalide');
            navigate('/login');
        }
    }, [token, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            toast.error('Les mots de passe ne correspondent pas');
            return;
        }

        if (password.length < 8) {
            toast.error('Le mot de passe doit contenir au moins 8 caractères');
            return;
        }

        setLoading(true);

        try {
            const response = await usersAPI.acceptInvite(token, password);

            // Auto-login with the returned token. Persist before navigating to
            // avoid a race where the protected /agent route reads an empty user
            // and bounces back to /login.
            if (response.data.access_token && response.data.user) {
                setSession(response.data.access_token, response.data.user);
                toast.success('Compte créé ! Connexion à votre espace…');
                // Hard reload to force the AuthContext to pick up the fresh token
                // and avoid React-state race conditions with route guards.
                setTimeout(() => { window.location.href = '/agent'; }, 400);
            } else {
                toast.success('Compte créé avec succès !');
                navigate('/login');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la création du compte');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2 text-center">
                Bienvenue dans l'équipe !
            </h2>
            <p className="text-sm text-muted-foreground mb-6 text-center">
                Créez votre mot de passe pour finaliser votre inscription
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <Label htmlFor="password">Mot de passe</Label>
                    <div className="relative">
                        <Input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Min. 8 caractères"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            minLength={8}
                            data-testid="invite-password-input"
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                </div>

                <div>
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        data-testid="invite-confirm-password-input"
                    />
                </div>

                <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="invite-submit-button"
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Création du compte...
                        </>
                    ) : (
                        'Créer mon compte'
                    )}
                </Button>
            </form>

            <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                    Déjà un compte ?{' '}
                    <Link to="/login" className="text-primary font-medium hover:underline">
                        Se connecter
                    </Link>
                </p>
            </div>
        </div>
    );
}

export default AcceptInvitePage;
